import { createIndexedDbLocalStore, hasIndexedDb, type WasmLocalStore } from './localStore'
import type {
    CreatedReferrer,
    ReferralClinicianDetail,
    ReferralClinicianList,
    ReferralPage,
    ReferralPair,
    ReferralPracticeMatrix,
    ReferralTargetList,
    ReferrerDetail,
    ReferrerList,
    ReferrerRecord,
    ReferralShareAs,
    SharedReferralCode,
    MadeReferralCode,
    ReferrerAccountCandidate,
    ReferrerAccount,
} from './referralTypes'

// Singleton loader for the Go WASM module. Returns a cached Promise
// so callers from multiple pages/components share one runtime instance
// and don't re-fetch the 11 MB blob.

// Webpack's DefinePlugin inlines GATSBY_* env vars at build time as
// string literals for both SSR and browser bundles — no runtime
// `typeof process` guard needed. A previous version kept that guard
// and quietly short-circuited to 'latest' in the browser whenever
// the pin was anything other than 'latest'.
export const deviceWasmVersion =
    (process.env.GATSBY_DEVICE_WASM_VERSION as string | undefined) || 'latest'
export const wasmBase = `/wasm/${deviceWasmVersion}`

/** Provider-matching reference data as served by the API (server/refdata):
 *  rows keep the data files' snake_case keys. */
// What the NPI Registry offers for an NPI. SUGGESTIONS a human accepts, amends
// or ignores — the editor never applies them on their behalf, because the
// background sweep exists to compare what a practice authored AGAINST the
// registry, and an import would leave it comparing the registry with a copy of
// itself.
export type NppesLookupResult = {
    success: boolean
    error?: string
    found: boolean
    /** The registry record's own date. NPPES is self-attested and never
     *  re-attested, so this is a staleness signal, not a guarantee. */
    lastUpdated?: string
    /** Absent when no display name was supplied — no verdict, not a negative. */
    surnameMatches?: boolean
    /** One row per registry entry, in registry order — the same tuple the
     *  profile stores: the code (empty when the entry's code is one we cannot
     *  store and only its licence is offered), OUR display name, the licence
     *  it is filed under, and the registry's primary flag (never on a
     *  code-less row). */
    taxonomies: NppesSuggestedCredential[]
}

export type NppesSuggestedCredential = {
    code: string
    displayName: string
    licenseState?: string
    licenseNumber?: string
    primary: boolean
}

/** One rendered page of an unpublished profile's public pages, and every
 *  page it would appear on. The HTML is self-contained and marked a preview
 *  on every page: a banner, noindex, an inert CTA, no structured data. */
export type ProviderPreview = {
    success: boolean
    error?: string
    html?: string
    page?: string
    pages: Array<{ kind: string; label: string; path: string }>
    /** What the publish gate would refuse; the preview renders regardless. */
    incompleteReasons?: string[]
}

export type ProviderPreviewLinkMint = {
    success: boolean
    error?: string
    tokenId?: string
    url?: string
    expiresAt?: string
}

export type ProviderPreviewLink = {
    tokenId: string
    profileId: string
    label?: string
    mintedBy?: string
    expiresAt: string
    revokedAt?: string
    revokedBy?: string
    viewCount: number
    maxViews: number
    firstViewedAt?: string
    lastViewedAt?: string
    createdAt: string
    live: boolean
    expired: boolean
}

export type ReferenceData = {
    taxonomyVersion: string
    /** `category` is the match category the code implies (the editor's
     *  specialty suggestion), derived server-side; absent when it implies
     *  none. */
    taxonomyCodes: Array<{ code: string; grouping: string; classification: string; specialization?: string; display_name: string; category?: string }>
    licenseClasses: Array<{ class: string; prescriber: boolean }>
    licensePrefixes: Array<{ prefix: string; class: string; examples?: string[] }>
    /** The payer master (practicemd-api docs/Payer-Master-Plan.md). `featured`
     *  is derived (an Ava chip in any state's list) and kept for older
     *  readers; `ava_chips` says where. `offered_to_categories` limits which
     *  providers' pickers offer the row; `served_by` records whose provider
     *  contracts serve its members, for the match categories in each
     *  relation's `for_categories` (none: every category).
     *  `status` is absent on an active row; a retired row names the row its
     *  names, resolution and matching redirect to in `retired_into`. */
    payers: Array<{
        payer_id: string
        display_name: string
        chip_label?: string
        aliases: string[]
        program: string
        state_scope: string[]
        offered_to_categories?: string[]
        served_by?: Array<{ payer_id: string; for_categories?: string[] }>
        shared_network?: boolean
        sources?: Array<{ kind: string; url?: string; ref?: string; quote?: string; supports?: string; verified: string }>
        ava_chips?: Array<{ state: string; rank: number }>
        featured: boolean
        status?: string
        retired_into?: string
    }>
    vocabularies: Array<{
        vocabulary_id: string
        categories: string[]
        terms: Array<{ term_id: string; kind: 'condition' | 'service'; label: string; synonyms: string[]; allowed_license_classes?: string[] }>
    }>
}

/** A practice in the booking directory (§5.17.1) — never a recipient. */
export interface PracticeProfile {
    id: string
    slug: string
    display: Record<string, any>
    name: string
    memberCount: number
    listedCount: number
    /** The practice's own short link and card; absent while no member is
     *  listed. */
    profileUrl?: string
    profileCardUrl?: string
    createdAt: string
    updatedAt: string
}

/** What practice.md/book/<slug> resolves to. */
export interface BookLink {
    kind: 'provider' | 'practice' | 'none'
    profile?: Record<string, any> | null
    practice?: PracticeProfile | null
}

export interface PracticemdWASM {
    // Build info baked into the WASM binary (set via ldflags by the
    // Makefile). Useful on an About page so the running bytes can
    // self-identify.
    version: string
    buildtime: string
    heartbeat: (endpoint: string, seedHex: string) => Promise<any>
    generateMasterKeyAndSeedEnvelope: (
        seedHex: string
    ) => Promise<{ seedEnvelopeB64: string; masterKeyHex: string }>
    wrapMasterKeyWithPassphrase: (masterKeyHex: string, passphrase: string) => Promise<string>
    unlockMasterKey: (passphraseEnvelopeB64: string, passphrase: string) => Promise<string>
    decryptX25519Envelope: (seedHex: string, envelopeJSON: string) => Promise<string>
    deviceKidsForSeed: (seedHex: string) => Promise<{
        seedKid: string
        sigKid: string
        encKid: string
        aesKid: string
    }>
    encryptWithDeviceAes: (
        seedHex: string,
        plaintextB64: string,
    ) => Promise<{ success: boolean; error: string; encryptedJSON: string }>
    decryptEncryptedItem: (
        seedHex: string,
        keyringB64: string,
        encryptedJSON: string,
    ) => Promise<{ success: boolean; error: string; plaintextB64: string; keyringB64: string }>
    pullKeyring: (
        endpoint: string,
        seedHex: string,
        existingKeyringB64: string
    ) => Promise<{ keyringB64: string; added: number; skipped: number; total: number }>
    readSave: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        saveId: string
    ) => Promise<{
        success: boolean
        error: string
        id: string
        identityId: string
        itemType: string
        metadata: Record<string, any> | null
        authorSigKid: string
        createdAt: string
        dataB64: string
        keyringB64: string
    }>
    writeSave: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        identityId: string,
        itemType: string,
        plaintextB64: string,
        metadataJSON: string,
        existingId: string
    ) => Promise<{
        success: boolean
        saveId: string
        error: string
        keyringB64: string
    }>
    listSaves: (
        endpoint: string,
        seedHex: string,
        argsJSON: string
    ) => Promise<{
        items: Array<{
            id: string
            identityId: string
            itemType: string
            metadata: Record<string, any> | null
            authorSigKid: string
            createdAt: string
        }>
        nextCursor: string
        totalCount: number
    }>
    deleteSave: (
        endpoint: string,
        seedHex: string,
        saveId: string
    ) => Promise<{ success: boolean; error: string }>
    rotatePuk: (
        endpoint: string,
        seedHex: string,
        keyringB64: string
    ) => Promise<{
        success: boolean
        newSeedKid: string
        newSigKid: string
        newEncKid: string
        newAesKid: string
        devicesTotal: number
        distributionSuccesses: number
        distributionFailures: number
        distributionErrors: string[]
        keyringB64: string
    }>
    writeSend: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        itemType: string,
        plaintextB64: string,
        recipientsJSON: string,
        metadataJSON: string
    ) => Promise<{
        success: boolean
        error: string
        sendId: string
        ephemeralFragments: Record<string, string>
        keyringB64: string
    }>
    writeSendAnonymous: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        itemType: string,
        plaintextB64: string,
        recipientsJSON: string,
        metadataJSON: string
    ) => Promise<{
        success: boolean
        error: string
        sendId: string
        keyringB64: string
    }>
    bindAnonymousFormSubmission: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        sendId: string
    ) => Promise<{
        success: boolean
        error: string
        keyringB64: string
    }>
    readSend: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        sendId: string,
        ephemeralFragment: string,
        recoveryHintIdentityIds?: string
    ) => Promise<{
        success: boolean
        error: string
        emailVerificationRequired: boolean
        id: string
        itemType: string
        createdAt: string
        metadata: Record<string, any> | null
        dataB64: string
        keyringB64: string
    }>
    readSendAnonymous: (
        endpoint: string,
        seedHex: string,
        sendId: string,
        fragment: string
    ) => Promise<{
        success: boolean
        error: string
        emailVerificationRequired: boolean
        id: string
        itemType: string
        createdAt: string
        metadata: Record<string, any> | null
        dataB64: string
    }>
    provisionWithAuthorizer: (
        endpoint: string,
        authorizerSeedHex: string,
        newDeviceSeedHex: string,
        deviceName: string,
        deviceType: string
    ) => Promise<{
        success: boolean
        error: string
        deviceId: string
        keyringB64: string
    }>
    provisionWithPairing: (
        endpoint: string,
        newDeviceSeedHex: string,
        // Optional all the way down: authorize.tsx passes `deviceName ||
        // undefined` when the field is blank, so undefined has always reached
        // the binding. Typing it required described a constraint nothing
        // enforced; widening it changes no runtime call.
        deviceName?: string
    ) => Promise<{
        success: boolean
        error: string
        pairingCode: string
        pairingId: string
        expiresIn: number
    }>
    intendToAddPaymentAuthorization: (
        endpoint: string,
        seedHex: string,
        practice: string,
        integrationId: string
    ) => Promise<Record<string, any>>
    getPaymentAuthorizationPdf: (
        endpoint: string,
        seedHex: string,
        id: string
    ) => Promise<string>
    addPaymentMethod: (
        endpoint: string,
        seedHex: string,
        argsJSON: string
    ) => Promise<{
        error?: Record<string, any>
        card?: {
            last4: string
            cardBrand: string
            expMonth: number
            expYear: number
            cardholderName: string
        }
        hasAgreementOnFile: boolean
        hasCardOnFile: boolean
        vendorInfo?: Record<string, any>
    }>
    storeEphemeralIdentity: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        fragment: string
    ) => Promise<{ success: boolean; error: string; keyringB64: string }>
    getSavedForm: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        formId: string
    ) => Promise<{
        success: boolean
        error: string
        save?: Record<string, any>
        keyringB64: string
    }>
    // Payment vendors
    listPaymentVendors: (
        endpoint: string,
        seedHex: string,
        identityId: string
    ) => Promise<{ items: Array<Record<string, any>> }>
    getPaymentVendor: (
        endpoint: string,
        seedHex: string,
        integrationId: string,
        identityId: string,
        vendorType: string
    ) => Promise<{ vendor: Record<string, any> | null }>
    deletePaymentVendor: (
        endpoint: string,
        seedHex: string,
        identityId: string,
        integrationId: string,
        vendorType: string
    ) => Promise<{ success: boolean; error: string }>
    validatePaymentVendor: (
        endpoint: string,
        seedHex: string,
        identityId: string,
        integrationId: string
    ) => Promise<{ success: boolean; error: string; merchantId: string; businessName: string }>
    createSquareOAuthUrl: (
        endpoint: string,
        seedHex: string,
        identityId: string,
        integrationId: string
    ) => Promise<{ success: boolean; error: string; authorizationUrl: string }>
    completeSquareOAuth: (
        endpoint: string,
        seedHex: string,
        code: string,
        state: string
    ) => Promise<{ success: boolean; error: string; vendor?: Record<string, any> }>
    // Billing
    listPlans: (
        endpoint: string,
        seedHex: string,
        includeInactive: boolean
    ) => Promise<{ items: Array<Record<string, any>> }>
    getMyLicenses: (
        endpoint: string,
        seedHex: string,
        activeOnly: boolean
    ) => Promise<{ items: Array<Record<string, any>> }>
    getMyUsage: (
        endpoint: string,
        seedHex: string,
        subscriptionId: string
    ) => Promise<Record<string, any>>
    subscribeMe: (
        endpoint: string,
        seedHex: string,
        argsJSON: string
    ) => Promise<Record<string, any>>
    changeSubscriptionPlan: (
        endpoint: string,
        seedHex: string,
        subscriptionId: string,
        newPlanId: string,
        billingInterval: string
    ) => Promise<Record<string, any>>
    cancelSubscription: (
        endpoint: string,
        seedHex: string,
        subscriptionId: string,
        immediate: boolean
    ) => Promise<Record<string, any>>
    reactivateSubscription: (
        endpoint: string,
        seedHex: string,
        subscriptionId: string
    ) => Promise<Record<string, any>>
    createFormTemplate: (
        endpoint: string,
        seedHex: string,
        templateJSON: string,
        createForIdentityId: string,
        selectedSubscriptionId: string
    ) => Promise<{
        success: boolean
        error: string
        claimed: boolean
        limitExceeded: boolean
        limitMessage: string
        upgradeCTA: string
        template?: Record<string, any>
    }>
    updateFormTemplate: (
        endpoint: string,
        seedHex: string,
        templateJSON: string
    ) => Promise<{ success: boolean; error: string }>
    updateFormTemplateName: (
        endpoint: string,
        seedHex: string,
        id: string,
        name: string
    ) => Promise<{ success: boolean; error: string }>
    updateFormTemplateMarks: (
        endpoint: string,
        seedHex: string,
        id: string,
        marksJSON: string,
        presubmitConditionsJSON: string
    ) => Promise<{ success: boolean; error: string }>
    updateFormTemplatePDF: (
        endpoint: string,
        seedHex: string,
        id: string,
        pdfJSON: string
    ) => Promise<{ success: boolean; error: string }>
    updateFormTemplateRecipient: (
        endpoint: string,
        seedHex: string,
        id: string,
        recipientUserId: string
    ) => Promise<{ success: boolean; error: string }>
    updateFormTemplateAllowAnonymous: (
        endpoint: string,
        seedHex: string,
        id: string,
        allow: boolean
    ) => Promise<{ success: boolean; error: string }>
    deleteFormTemplate: (
        endpoint: string,
        seedHex: string,
        id: string,
        force: boolean
    ) => Promise<{ success: boolean; error: string }>
    claimFormTemplate: (
        endpoint: string,
        seedHex: string,
        argsJSON: string
    ) => Promise<{ success: boolean; error: string }>
    listFormTemplates: (
        endpoint: string,
        seedHex: string,
        argsJSON: string
    ) => Promise<{
        items: Array<Record<string, any>>
        nextCursor: string
        totalCount: number
        error: string
    }>
    readFormTemplatePdf: (
        endpoint: string,
        seedHex: string,
        sha256: string,
        templateId: string
    ) => Promise<{ success: boolean; error: string; pdfB64: string }>
    assignPaymentIntegrationToTemplate: (
        endpoint: string,
        seedHex: string,
        templateId: string,
        paymentIntegrationId: string
    ) => Promise<{ success: boolean; error: string }>
    createTeam: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        teamName: string
    ) => Promise<{
        success: boolean
        error: string
        teamId: string
        name: string
        keyringB64: string
    }>
    addUserToTeam: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        teamId: string,
        userIdOrEmail: string,
        permissionsJSON: string
    ) => Promise<{ success: boolean; error: string }>
    updateMemberPermissions: (
        endpoint: string,
        seedHex: string,
        teamId: string,
        userId: string,
        permissionsJSON: string
    ) => Promise<{ success: boolean; error: string }>
    removeMemberFromTeam: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        teamId: string,
        userIdOrEmail: string
    ) => Promise<{ success: boolean; error: string; keyringB64: string }>
    deleteTeam: (
        endpoint: string,
        seedHex: string,
        teamId: string
    ) => Promise<{ success: boolean; error: string }>
    listTeams: (
        endpoint: string,
        seedHex: string
    ) => Promise<{
        items: Array<{
            id: string
            name: string
            createdAt: string
            myPermissions: Array<{ type: string; allowed?: boolean; limit?: number }>
        }>
    }>
    listTeamMembers: (
        endpoint: string,
        seedHex: string,
        teamId: string
    ) => Promise<{
        items: Array<{
            userId: string
            name: string
            email: string
            permissions: Array<{ type: string; allowed?: boolean; limit?: number }>
        }>
    }>
    inviteExistingUserToTeam: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        teamId: string,
        inviteeIdOrEmail: string,
        permissionsJSON: string
    ) => Promise<{
        success: boolean
        invitationId: string
        itemId: string
        error: string
    }>
    acceptTeamInvitation: (
        endpoint: string,
        seedHex: string,
        itemId: string
    ) => Promise<{ success: boolean; teamId: string; error: string }>
    declineTeamInvitation: (
        endpoint: string,
        seedHex: string,
        itemId: string
    ) => Promise<{ success: boolean; error: string }>
    withdrawTeamInvitation: (
        endpoint: string,
        seedHex: string,
        pendingId: string
    ) => Promise<{ success: boolean; error: string }>
    requestToJoinTeam: (
        endpoint: string,
        seedHex: string,
        teamId: string,
        message: string
    ) => Promise<{ success: boolean; requestId: string; error: string }>
    approveJoinRequest: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        pendingId: string,
        teamId: string,
        requesterUserId: string
    ) => Promise<{ success: boolean; teamId: string; error: string }>
    denyJoinRequest: (
        endpoint: string,
        seedHex: string,
        pendingId: string
    ) => Promise<{ success: boolean; error: string }>
    listTeamJoinRequests: (
        endpoint: string,
        seedHex: string,
        teamId: string
    ) => Promise<{
        items: Array<{
            pendingId: string
            requesterUserId: string
            requesterEmail: string
            requesterName: string
            message: string
            createdAt: number
            expiresAt: number
        }>
    }>
    inviteEmailToTeam: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        teamId: string,
        inviteeEmail: string,
        siteUrl: string,
        permissionsJSON: string
    ) => Promise<{ success: boolean; invitationId: string; accessUrl: string; emailError: string; error: string }>
    claimEmailInvitation: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        pendingId: string,
        fragment: string
    ) => Promise<{ success: boolean; teamId: string; error: string }>
    listPendingIbms: (
        endpoint: string,
        seedHex: string
    ) => Promise<{
        items: Array<{
            id: string
            category: string
            body: string
            state: string
            createdAt: number
            expiresAt: number
            dismissedAt: number
            ackAt: number
        }>
    }>
    notificationCenterItems: (
        endpoint: string,
        seedHex: string
    ) => Promise<{
        items: Array<{
            id: string
            category: string
            body: string
            refTable: string
            refId: string
            state: string
            seq: number
            createdAt: number
            expiresAt: number
            anyDeviceAck: boolean
        }>
    }>
    notificationUnreadCount: (
        endpoint: string,
        seedHex: string
    ) => Promise<{ count: number }>
    dismissStateItem: (
        endpoint: string,
        seedHex: string,
        itemId: string
    ) => Promise<{ success: boolean }>
    listTeamPendingInvitations: (
        endpoint: string,
        seedHex: string,
        teamId: string
    ) => Promise<{
        items: Array<{
            pendingId: string
            inviteeUserId: string
            inviteeEmail: string
            inviteeName: string
            permissions: string[]
            createdAt: number
            expiresAt: number
        }>
    }>
    handlePtkRotations: (
        endpoint: string,
        seedHex: string,
        keyringB64: string
    ) => Promise<{ handled: number; keyringB64: string; error: string }>
    requestPairing: (
        endpoint: string,
        seedHex: string,
        deviceName: string
    ) => Promise<{
        success: boolean
        error: string
        pairingId: string
        pairingCode: string
        expiresIn: number
    }>
    checkPairingStatus: (
        endpoint: string,
        seedHex: string,
        pairingId: string
    ) => Promise<{
        status: string
        isCompleted: boolean
        expiresIn: number
    }>
    approvePairing: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        pairingCode: string,
        deviceName: string
    ) => Promise<{
        success: boolean
        error: string
        deviceName: string
        keyringB64: string
    }>
    listDevices: (
        endpoint: string,
        seedHex: string,
        keyringB64: string
    ) => Promise<{
        items: Array<{
            id: string
            name: string
            deviceType: string
            sigKid: string
            encKid: string
            createdAt: string
            lastSeenAt: string
            isCurrentDevice: boolean
            online: boolean
            pxkStatus?: {
                totalPresent: number
                totalInUniverse: number
                isComplete: boolean
                missingPxks: Array<{ identityId: string; identityType: string; seedKid: string }>
            }
        }>
    }>
    revokeDevices: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        sigKidsJSON: string,
        onProgress?: (ev: { step: string; message: string; current: number; total: number }) => void,
    ) => Promise<{ success: boolean; error: string; keyringB64: string }>
    pushKeys: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        onProgress?: (ev: { step: string; message: string; current: number; total: number }) => void,
    ) => Promise<{ success: boolean; error: string; distributed: number; keyringB64: string }>
    createUser: (
        endpoint: string,
        seedHex: string,
        userId: string,
        email: string,
        deviceName: string
    ) => Promise<{
        success: boolean
        verificationSent: boolean
        error: string
        userId: string
        email: string
        pukSeedHex: string
    }>
    completeUserCreation: (
        endpoint: string,
        seedHex: string,
        userId: string,
        pukSeedHex: string,
        code: string
    ) => Promise<{
        success: boolean
        error: string
        userId: string
        keyringB64: string
    }>
    provePaperKeyPossession: (
        endpoint: string,
        paperSeedHex: string
    ) => Promise<{
        success: boolean
        provenAt: string
        error: string
    }>
    mnemonicToSeed: (words: string) => Promise<{
        success: boolean
        error: string
        seedHex: string
        sigKid: string
        encKid: string
    }>
    verifyCreationCode: (
        endpoint: string,
        seedHex: string,
        userId: string,
        code: string
    ) => Promise<{
        success: boolean
        error: string
    }>
    /** Release 1: recoveryJSON is a JSON array of
     *  {kind: 'paper'|'passkey', seedHex, name?, credentialId?, publicKey?, envelope?};
     *  an empty array commits a patient guest. */
    finalizeUserCreation: (
        endpoint: string,
        seedHex: string,
        userId: string,
        pukSeedHex: string,
        code: string,
        recoveryJSON: string
    ) => Promise<{
        success: boolean
        error: string
        userId: string
        keyringB64: string
    }>
    /** A passkey recovery device's key material: a paper key with no words. */
    prepareRecoveryDevice: () => Promise<{
        seedHex: string
        seedKid: string
        sigKid: string
        encKid: string
        aesKid: string
    }>
    /** HKDF of the passkey's PRF output → the recovery key (hex). */
    deriveRecoveryKey: (prfOutputHex: string) => Promise<string>
    wrapRecoverySeed: (seedHex: string, recoveryKeyHex: string) => Promise<string>
    unwrapRecoverySeed: (envelopeB64: string, recoveryKeyHex: string) => Promise<string>
    registerPasskeyDevice: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        recoverySeedHex: string,
        name: string,
        credentialId: string,
        publicKeyB64: string,
        envelopeB64: string
    ) => Promise<{ success: boolean; error: string; keyringB64: string }>
    /** PUBLIC: the assertion's parts base64url, as the browser spells them. */
    recoveryEnvelope: (
        endpoint: string,
        credentialId: string,
        clientDataJSON: string,
        authenticatorData: string,
        signature: string
    ) => Promise<{ found: boolean; envelope: string }>
    deriveDeviceKids: (seedHex: string) => Promise<{
        seedKid: string
        sigKid: string
        encKid: string
        aesKid: string
    }>
    preparePaperKey: () => Promise<{
        seedHex: string
        mnemonic: string[]
        seedKid: string
        sigKid: string
        encKid: string
        aesKid: string
    }>
    registerPaperKey: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        paperKeySeedHex: string,
        paperKeyName: string
    ) => Promise<{ success: boolean; error: string; keyringB64: string }>
    generatePaperKey: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        paperKeyName: string
    ) => Promise<{
        success: boolean
        error: string
        mnemonic: string[]
        seedKid: string
        sigKid: string
        encKid: string
        aesKid: string
        keyringB64: string
    }>
    updateUserInfo: (
        endpoint: string,
        seedHex: string,
        updatesJSON: string
    ) => Promise<{
        success: boolean
        error: string
        emailUnavailable: boolean
        emailChallenge: boolean
        userinfo?: {
            id: string
            email: string
            emailVerified: boolean
            name: string
            givenName: string
            familyName: string
            nickname: string
        }
    }>
    updateRecipientState: (
        endpoint: string,
        seedHex: string,
        sendId: string,
        state: string,
        ephemeralIdentityId: string
    ) => Promise<{ success: boolean; error: string }>
    claimSendAccess: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        sendId: string,
        fragment: string
    ) => Promise<{
        success: boolean
        error: string
        grantId: string
        keyringB64: string
    }>
    requestEphemeralEmailVerification: (
        endpoint: string,
        seedHex: string,
        sendId: string,
        ephemeralIdentityId: string
    ) => Promise<{ success: boolean; error: string }>
    verifyEphemeralAccess: (
        endpoint: string,
        seedHex: string,
        sendId: string,
        ephemeralIdentityId: string,
        code: string
    ) => Promise<{ success: boolean; error: string }>
    sendEphemeralAccessEmail: (
        endpoint: string,
        seedHex: string,
        argsJSON: string
    ) => Promise<{ success: boolean; error: string }>
    listSends: (
        endpoint: string,
        seedHex: string,
        argsJSON: string
    ) => Promise<{
        items: Array<{
            id: string
            itemType: string
            senderSigKid: string
            recipientEncKid: string
            metadata: Record<string, any> | null
            createdAt: string
            recipientData: { status: string; statusChangedAt: string } | null
            sender: { identityId: string; identityType: string; displayName: string } | null
            recipient: { identityId: string; identityType: string; displayName: string } | null
            recipients: Array<{ identityId: string; identityType: string; displayName: string }> | null
        }>
        nextCursor: string
        totalCount: number
    }>
    // Registers the browser LocalStore (localStore.ts) with the shared
    // library; null unregisters. Called by the loader on first load.
    setLocalStore: (store: WasmLocalStore | null) => void
    // Positional by-kid batch form of getIdentity; null per unknown kid.
    // seedHex, when given, enables the sealed cache tier.
    getIdentities: (
        endpoint: string,
        kidsJSON: string,
        seedHex?: string
    ) => Promise<Array<{
        id: string
        seedKid: string
        sigKid: string
        encKid: string
        aesKid: string
        type?: string
        name?: string
        givenName?: string
        familyName?: string
    } | null>>
    getIdentity: (
        endpoint: string,
        key: string,
        seedHex?: string
    ) => Promise<{
        id: string
        seedKid: string
        sigKid: string
        encKid: string
        aesKid: string
        type?: string
        name?: string
        givenName?: string
        familyName?: string
    }>
    getUserInfo: (
        endpoint: string,
        seedHex: string
    ) => Promise<{
        id: string
        name: string
        given_name: string
        family_name: string
        email: string
        email_verified: boolean
        phone: string
        nickname: string
        seedKid: string
        sigKid: string
        encKid: string
        aesKid: string
        hasRecoveryDevice: boolean
        recoveryDueAt: string
    }>
    // Admin
    getAdminStatus: (
        endpoint: string,
        seedHex: string
    ) => Promise<{ isAdmin: boolean }>
    // Provider profiles (booking marketplace, §5.18/§16)
    listManagedProviderProfiles: (
        endpoint: string,
        seedHex: string,
        identityId: string
    ) => Promise<{ items: Array<Record<string, any>> }>
    listAllProviderProfiles: (
        endpoint: string,
        seedHex: string
    ) => Promise<{ items: Array<Record<string, any>> }>
    // Vetted provider-profile proposals: assigned managers see + propose;
    // Practice MD reviews per-field. All gates server-side.
    hasProviderProfileManageAccess: (
        endpoint: string,
        seedHex: string
    ) => Promise<{ hasAccess: boolean }>
    // The Referrals nav gate: would the referral page have a section?
    hasReferralPage: (endpoint: string, seedHex: string) => Promise<{ hasAccess: boolean }>
    listMyManagedProviderProfiles: (
        endpoint: string,
        seedHex: string
    ) => Promise<{
        profiles: Array<Record<string, any>>
        identities: Array<{ identityId: string; kind: 'user' | 'team' }>
    }>
    listMyProviderProfileChanges: (
        endpoint: string,
        seedHex: string,
        limit: number
    ) => Promise<{ requests: Array<Record<string, any>> }>
    submitProviderProfileChange: (
        endpoint: string,
        seedHex: string,
        profileId: string,
        identityId: string,
        slug: string,
        displayJSON: string
    ) => Promise<{ request: Record<string, any>; superseded?: string }>
    resubmitProviderProfileChange: (
        endpoint: string,
        seedHex: string,
        requestId: string,
        slug: string,
        displayJSON: string
    ) => Promise<{ request: Record<string, any> }>
    adminListProviderProfileChanges: (
        endpoint: string,
        seedHex: string,
        status: string,
        limit: number,
        offset: number
    ) => Promise<{ requests: Array<Record<string, any>> }>
    // A staleness refusal RESOLVES with staleKeys (the review re-renders those
    // rows against today's live values); other failures reject.
    adminApproveProviderProfileChange: (
        endpoint: string,
        seedHex: string,
        requestId: string,
        reviewJSON: string
    ) => Promise<{
        request?: Record<string, any>
        profile?: Record<string, any>
        staleKeys?: string[]
    }>
    adminRejectProviderProfileChange: (
        endpoint: string,
        seedHex: string,
        requestId: string,
        message: string,
        note: string
    ) => Promise<{ request: Record<string, any> }>
    reassignProviderProfileRecipient: (
        endpoint: string,
        seedHex: string,
        profileId: string,
        identityId: string
    ) => Promise<Record<string, any>>
    // Demand brokerage console (admin) — docs/Demand-Brokerage-Architecture.md.
    // The filter rides as ONE JSON object rather than eight positional args:
    // positional booleans are where a caller transposes two flags, and there are
    // four here. `isMinor` must be omitted (not false) to mean "either".
    adminListOpenDemand: (
        endpoint: string,
        seedHex: string,
        filterJSON: string
    ) => Promise<{ items: Array<Record<string, any>> }>
    // The onboarding follow-up queue (docs/Growth-Event-Alerting.md, O4).
    // Positional rather than a filter object: one argument, so there is no pair
    // of same-typed flags for a caller to transpose.
    adminListOnboardingRequests: (
        endpoint: string,
        seedHex: string,
        limit: number
    ) => Promise<{ items: Array<Record<string, any>> }>
    // Recent accounts, newest first. Staff admins only, enforced server-side
    // (admin grant AND staff-team member).
    adminListRecentAccounts: (
        endpoint: string,
        seedHex: string,
        limit: number
    ) => Promise<{ items: Array<Record<string, any>> }>
    // The whole submitted document, truncated SERVER side so a base64 headshot
    // never reaches the browser at all.
    adminGetOnboardingRequest: (
        endpoint: string,
        seedHex: string,
        id: string
    ) => Promise<{ summary: string }>
    // Web Push (practicemd-api docs/Growth-Event-Alerting.md §6). Every op is
    // scoped SERVER-side to the calling device; the public key is unsigned
    // because it is public by design.
    registerPushToken: (
        endpoint: string,
        seedHex: string,
        subscriptionJSON: string
    ) => Promise<{ ok: boolean }>
    revokePushToken: (endpoint: string, seedHex: string, token: string) => Promise<{ ok: boolean }>
    listMyPushTokens: (
        endpoint: string,
        seedHex: string
    ) => Promise<{ items: Array<{ platform: string; token: string; registeredAt: string }> }>
    pushPublicKey: (endpoint: string) => Promise<{ publicKey: string }>
    // Records that a person followed the request up and ends its notification
    // on every staff device. Resolves to the time it was FIRST recorded.
    adminMarkOnboardingFollowedUp: (
        endpoint: string,
        seedHex: string,
        id: string
    ) => Promise<{ followedUpAt: string }>
    // detail is null when the search never reached a match — a real answer, not
    // an error: there is simply nothing to broker.
    adminGetDemandDetail: (
        endpoint: string,
        seedHex: string,
        searchId: string
    ) => Promise<{ detail: Record<string, any> | null }>
    // The recorded matched-provider set for one demand row (what the patient
    // was shown, written at match time).
    adminDemandMatches: (
        endpoint: string,
        seedHex: string,
        searchId: string
    ) => Promise<{ matches: Array<Record<string, any>> }>
    // Resolves with success:false + a reason for a policy refusal (stale demand,
    // a pinned direct-link search, a closed search) rather than rejecting — the
    // reason contains the next step and has to reach the operator.
    setProviderProfileListed: (
        endpoint: string,
        seedHex: string,
        profileId: string,
        listed: boolean
    ) => Promise<{ listed: boolean }>
    adminMintDemandOutreachToken: (
        endpoint: string,
        seedHex: string,
        searchId: string,
        label: string,
        ttlHours: number,
        // Trailing and optional on the Go side too, so an older bundle calling with
        // five arguments still works against a newer wasm.
        profileId?: string,
        recipientIdentityId?: string
    ) => Promise<{
        success: boolean
        error?: string
        tokenId?: string
        url?: string
        expiresAt?: string
        // "board" when it also landed as an item on the recipient's Appointment
        // Requests board; "url" when the URL is its only carrier.
        delivery?: string
    }>
    adminRevokeDemandOutreachToken: (
        endpoint: string,
        seedHex: string,
        tokenId: string
    ) => Promise<{ success: boolean }>
    // The staff review queue and the vouch. `argsJSON` rather than eight
    // positional arguments for the same reason as the filter above — and because
    // two of the review's inputs are structured.
    adminListInterestSubmissions: (
        endpoint: string,
        seedHex: string,
        filterJSON: string
    ) => Promise<{ items: Array<Record<string, any>> }>
    // Carries CHOICES, never content: `accepted` maps a display key to
    // "theirs"/"ours" and the server assembles the result from the two values it
    // already holds. Resolves with success:false + the server's reason for a
    // policy refusal (unread prose, a missing profile) rather than rejecting.
    adminApproveInterestSubmission: (
        endpoint: string,
        seedHex: string,
        argsJSON: string
    ) => Promise<{ success: boolean; error?: string; recommendationsQueued?: number }>
    adminRejectInterestSubmission: (
        endpoint: string,
        seedHex: string,
        interestId: string,
        reason: string,
        // What the practice reads on their own board, verbatim. No email carries a
        // review outcome, so an empty one leaves them knowing nothing.
        message: string
    ) => Promise<{ success: boolean }>
    adminAskStillLooking: (
        endpoint: string,
        seedHex: string,
        searchId: string
    ) => Promise<{ success: boolean; error?: string; asked?: boolean; alreadyAsked?: boolean }>
    // profileIds crosses as a comma-separated string: the wasm boundary has no
    // array type.
    recommendProvidersToSearch: (
        endpoint: string,
        seedHex: string,
        searchId: string,
        profileIdsCSV: string,
        origin?: string
    ) => Promise<{ success: boolean; error?: string; queued?: number }>
    // The PRACTICE's own view: patients staff put in front of them, interest submissions
    // they made, and searches they currently match. De-identified.
    demandItemsForIdentity: (
        endpoint: string,
        seedHex: string,
        identityId?: string
    ) => Promise<{ items: Array<Record<string, any>> }>
    respondToDemandInvitation: (
        endpoint: string,
        seedHex: string,
        tokenId: string,
        accept: boolean,
        note?: string
    ) => Promise<{ ok: boolean; error?: string; interestId?: string; alreadySubmitted?: boolean }>
    resubmitDemandInterest: (
        endpoint: string,
        seedHex: string,
        interestId: string,
        payloadJSON?: string,
        draftDisplayJSON?: string
    ) => Promise<{ ok: boolean; error?: string; interestId?: string; revision?: number }>
    // The tokened outreach page once its visitor has an account. The ANONYMOUS
    // open is not here at all: /interest fetches it with a plain POST and loads no
    // wasm, which is what keeps an acquisition page free of a device layer a
    // stranger has no use for.
    openDemandOutreachLinkAsSelf: (
        endpoint: string,
        seedHex: string,
        tokenId: string,
        secret: string
    ) => Promise<{
        ok: boolean
        error?: string
        authed?: boolean
        onYourBoard?: boolean
        parkedDraft?: Record<string, any>
        view?: Record<string, any>
    }>
    submitDemandInterest: (
        endpoint: string,
        seedHex: string,
        tokenId: string,
        secret: string,
        payloadJSON: string,
        draftDisplayJSON?: string
    ) => Promise<{
        ok: boolean
        error?: string
        interestId?: string
        alreadySubmitted?: boolean
        // Refused ONLY for want of a session: run the auth flow and retry, rather
        // than showing an error.
        needsAuth?: boolean
    }>
    // Public directory lookup by slug (§5.17) — unsigned, no seed. null if unknown.
    getProviderProfile: (
        endpoint: string,
        slug: string
    ) => Promise<Record<string, any> | null>
    // Practice profiles (§5.17.1). resolveBookLink is public (no seed).
    resolveBookLink: (endpoint: string, slug: string) => Promise<BookLink>
    listPracticeProfiles: (endpoint: string, seedHex: string) => Promise<PracticeProfile[]>
    createPracticeProfile: (endpoint: string, seedHex: string, slug: string, displayJSON: string) => Promise<PracticeProfile>
    updatePracticeProfile: (endpoint: string, seedHex: string, practiceId: string, slug: string, displayJSON: string) => Promise<PracticeProfile>
    deletePracticeProfile: (endpoint: string, seedHex: string, practiceId: string) => Promise<boolean>
    setProviderProfilePractice: (endpoint: string, seedHex: string, profileId: string, practiceId: string) => Promise<Record<string, any>>
    arrangePracticeMembers: (endpoint: string, seedHex: string, practiceId: string, profileIds: string[]) => Promise<Record<string, any>[]>
    // Public, unsigned: the embedded provider-matching reference data
    // (NUCC taxonomy subset, license classes, payer master, vocabularies).
    getReferenceData: (endpoint: string) => Promise<ReferenceData>
    nppesLookup: (
        endpoint: string,
        seedHex: string,
        npi: string,
        displayName: string
    ) => Promise<NppesLookupResult>
    // The PUBLIC pages of a profile that is not published, rendered for
    // staff (admin-gated). The display rides as JSON.
    adminRenderProviderPreview: (
        endpoint: string,
        seedHex: string,
        profileId: string,
        displayJSON: string,
        page: string
    ) => Promise<ProviderPreview>
    // Preview links: a URL a clinician opens to see the same pages, no
    // account needed. The mint's `url` carries the ONLY copy of the secret.
    adminMintProviderPreviewLink: (
        endpoint: string,
        seedHex: string,
        profileId: string,
        label: string,
        ttlDays: number
    ) => Promise<ProviderPreviewLinkMint>
    adminRevokeProviderPreviewLink: (
        endpoint: string,
        seedHex: string,
        tokenId: string
    ) => Promise<{ success: boolean }>
    adminListProviderPreviewLinks: (
        endpoint: string,
        seedHex: string,
        profileId: string
    ) => Promise<{ success: boolean; links: ProviderPreviewLink[] }>
    createProviderProfile: (
        endpoint: string,
        seedHex: string,
        identityId: string,
        slug: string,
        displayJSON: string
    ) => Promise<Record<string, any>>
    updateProviderProfile: (
        endpoint: string,
        seedHex: string,
        profileId: string,
        identityId: string,
        slug: string,
        displayJSON: string
    ) => Promise<Record<string, any>>
    deleteProviderProfile: (
        endpoint: string,
        seedHex: string,
        profileId: string
    ) => Promise<{ success: boolean }>
    uploadProviderAsset: (
        endpoint: string,
        seedHex: string,
        contentBase64: string,
        contentType: string
    ) => Promise<{ url: string }>
    // Ava intake (booking marketplace, §7.1/§7.7/§16)
    previewSearchTurn: (endpoint: string, seedHex: string, entry?: string) => Promise<{ turn: Record<string, any> }>
    startAvaSearch: (
        endpoint: string,
        seedHex: string,
        targetProfileSlug?: string,
        utmJSON?: string
    ) => Promise<{
        searchId: string
        turn: Record<string, any>
        selfGrant: Record<string, any>
        stateSnapshot: Record<string, any>
        nextSeq: number
    }>
    // SYNCHRONOUS (no promise): a pure string function, and the web needs the
    // echo before it calls advanceAvaTurn. Shares the implementation that seals
    // the value, so rendered and sealed words cannot drift apart.
    userBubbleForAnswer: (answerJSON: string) => string
    // SYNCHRONOUS: the URL a patient's Share hands a friend (Referrals-
    // Architecture §10.3), from the library the CLI builds it with.
    shareLinkURL: (siteURL: string, slug: string, profileId: string) => string
    drainAvaPending: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        searchId: string,
        nodeId: string,
        selfGrantJSON: string,
        stateSnapshotJSON: string,
        seq: number
    ) => Promise<any>
    // Synchronous: a pure projection of decrypted intake state into the sealed
    // Details document (§6.3). Returns JSON, or "" if the state cannot be read.
    buildAppointmentRequestDetails: (stateJSON: string) => string
    // Reconnect pacing, shared with the CLI daemon. Synchronous: pure
    // arithmetic, needed between a drop and the next dial.
    nextReconnectAttempt: (prevAttempt: number, connectionLivedMs: number) => number
    reconnectBackoffMs: (attempt: number) => number
    // One inbound OOBM, routed by the shared policy. keyringB64 rides in and
    // out because the socket binding carries no keyring; persist the returned
    // one when keyringChanged. `error` is a string, never a rejection — a bad
    // frame must not tear down the socket loop.
    consumePushFrame: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        selfIdentityId: string,
        frameJSON: string
    ) => Promise<{
        action: string
        reason: string
        keyringChanged: boolean
        keyringB64: string
        userInfo: Record<string, any> | null
        rotationsHandled: number
        error: string
    }>
    // The §16.5 reconnect convergence.
    catchUpAfterConnect: (
        endpoint: string,
        seedHex: string,
        keyringB64: string
    ) => Promise<{
        pulledKeyring: boolean
        keyringChanged: boolean
        keyringB64: string
        userInfo: Record<string, any> | null
        rotationsHandled: number
        error: string
    }>
    // The §4.8 send_inquiries effect, interpreted in one shared-library call:
    // the encrypted fan-out first, then the chart advance sealing the selection
    // as the answer (device.SendInquiriesAndAdvance).
    sendInquiriesAndAdvance: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        searchId: string,
        nodeId: string,
        providersJSON: string,
        selfGrantJSON: string,
        stateSnapshotJSON: string,
        seq: number,
        revising: boolean,
        clientEvents?: string,
        stateOverridesJSON?: string
    ) => Promise<{
        sent: number
        withdrawn: number
        turn: Record<string, any>
        stateSnapshot: Record<string, any>
        nextSeq: number
        keyringB64?: string
    }>
    advanceAvaTurn: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        searchId: string,
        nodeId: string,
        answerJSON: string,
        selfGrantJSON: string,
        stateSnapshotJSON: string,
        seq: number,
        userBubble: string,
        // Comma-separated event families this client can drive (§4.11). Optional
        // so an older bundle declares nothing and stays on the legacy route.
        clientEvents?: string
    ,
        stateOverridesJSON?: string
    ) => Promise<{
        turn: Record<string, any>
        stateSnapshot: Record<string, any>
        nextSeq: number
        keyringB64: string
    }>
    // runAvaMatch: suggest → classify → signal → persist, in one device-layer
    // call. The outcome classification lives in the device library, not here,
    // so every client agrees on what "no providers" versus "the lookup failed"
    // means. `turn` is absent when the event was deferred.
    runAvaMatch: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        searchId: string,
        nodeId: string,
        selfGrantJSON: string,
        stateSnapshotJSON: string,
        seq: number
    ) => Promise<{
        outcome: string
        providers: Array<Record<string, any>>
        turn?: Record<string, any>
        stateSnapshot: Record<string, any>
        nextSeq: number
        keyringB64: string
    }>
    // drainAvaRecommendations: render providers staff have queued into the
    // patient's conversation. `turn` is absent when nothing was queued OR when the
    // conversation cannot accept the event yet (deferred) — `deferred` tells the two
    // apart. `ackError` means the recommendation IS sealed in the transcript but the
    // settle failed, so it may render again next drain; it is never a failure.
    drainAvaRecommendations: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        searchId: string,
        nodeId: string,
        selfGrantJSON: string,
        stateSnapshotJSON: string,
        seq: number
    ) => Promise<{
        drained: number
        providers: Array<Record<string, any>>
        origin: string
        deferred: boolean
        turn?: Record<string, any>
        stateSnapshot: Record<string, any>
        nextSeq: number
        keyringB64: string
        ackError?: string
    }>
    // The stale "are you still looking?" check-in. Shaped like the drain above,
    // and for the same reason: the transcript is sealed under a content key that
    // never leaves the device, so the server publishes the question and the client
    // writes the bubble.
    drainAvaStillLookingCheck: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        searchId: string,
        nodeId: string,
        selfGrantJSON: string,
        stateSnapshotJSON: string,
        seq: number
    ) => Promise<{
        rendered: boolean
        deferred: boolean
        turn?: Record<string, any>
        stateSnapshot?: Record<string, any>
        nextSeq: number
    }>
    // The ANSWER's durable half. Separate from the turn because a client that
    // rendered the question and skipped this would leave the patient's answer with
    // no effect at all. `withdrawn` lets the conversation say plainly that the
    // practices we contacted can no longer see the request.
    settleStillLookingCheck: (
        endpoint: string,
        seedHex: string,
        searchId: string,
        keepLooking: boolean
    ) => Promise<{ ok: boolean; withdrawn: boolean }>
    // Staff↔patient demand messaging (§8). The staff send takes the patient's
    // enc kid from getDemandThread so the console never resolves the patient
    // identity (and its name) itself; the drain seals "Practice MD" speech
    // turns into the conversation client-side (no chart movement).
    sendDemandMessage: (
        endpoint: string,
        seedHex: string,
        searchId: string,
        recipientIdentityId: string,
        recipientEncKid: string,
        selfIdentityId: string,
        messageB64: string
    ) => Promise<{ messageId: string; sendId: string; searchId: string }>
    getDemandThread: (
        endpoint: string,
        seedHex: string,
        searchId: string
    ) => Promise<Record<string, any> | null>
    drainAvaStaffMessages: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        searchId: string,
        nodeId: string,
        selfGrantJSON: string,
        stateSnapshotJSON: string,
        seq: number
    ) => Promise<{
        turns?: Array<Record<string, any>>
        stateSnapshot?: Record<string, any>
        nextSeq: number
    }>
    sendPatientStaffMessage: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        searchId: string,
        nodeId: string,
        text: string,
        selfGrantJSON: string,
        stateSnapshotJSON: string,
        seq: number
    ) => Promise<{
        messageId: string
        sendId: string
        turn: Record<string, any>
        stateSnapshot?: Record<string, any>
        nextSeq: number
    }>
    setDemandMessagesRead: (
        endpoint: string,
        seedHex: string,
        searchId: string,
        read: boolean
    ) => Promise<{ affected: number }>
    adminListUnreadDemandThreads: (
        endpoint: string,
        seedHex: string
    ) => Promise<Array<Record<string, any>>>
    adminDesignateStaffTeam: (
        endpoint: string,
        seedHex: string,
        teamId: string
    ) => Promise<{ teamId: string; teamName: string }>
    adminGetStaffTeam: (
        endpoint: string,
        seedHex: string
    ) => Promise<{ teamId: string; teamName: string }>
    /** The Payers page's operations (practicemd-api
     *  docs/Payer-Master-Plan.md §5.8, §7.1): each resolves the server's
     *  Dictionary; a refusal the staff member caused is data, ok false with
     *  error. Objects and lists cross as JSON strings. */
    adminCreatePayer: (
        endpoint: string,
        seedHex: string,
        payerJSON: string,
        fromResearchJSON: string
    ) => Promise<Record<string, any>>
    adminUpdatePayer: (
        endpoint: string,
        seedHex: string,
        payerJSON: string,
        fromResearchJSON: string
    ) => Promise<Record<string, any>>
    adminRetirePayer: (
        endpoint: string,
        seedHex: string,
        payerId: string,
        into: string
    ) => Promise<Record<string, any>>
    adminSetAvaPayerChips: (
        endpoint: string,
        seedHex: string,
        state: string,
        chipsJSON: string
    ) => Promise<Record<string, any>>
    adminImportPayers: (
        endpoint: string,
        seedHex: string,
        masterJSON: string,
        overwrite: boolean
    ) => Promise<Record<string, any>>
    adminPreviewPayerChange: (
        endpoint: string,
        seedHex: string,
        changeJSON: string
    ) => Promise<Record<string, any>>
    adminPayerUsedBy: (
        endpoint: string,
        seedHex: string,
        payerId: string
    ) => Promise<Record<string, any>>
    adminPayersByState: (
        endpoint: string,
        seedHex: string
    ) => Promise<Record<string, any>>
    adminPayerChangeLog: (
        endpoint: string,
        seedHex: string,
        payerId: string,
        limit: number
    ) => Promise<Record<string, any>>
    adminPayerResearchSources: (
        endpoint: string,
        seedHex: string,
        statesJSON: string
    ) => Promise<Record<string, any>>
    adminResearchPayer: (
        endpoint: string,
        seedHex: string,
        name: string,
        statesJSON: string
    ) => Promise<Record<string, any>>
    adminRecheckPayer: (
        endpoint: string,
        seedHex: string,
        payerId: string
    ) => Promise<Record<string, any>>
    adminResearchPayerAgain: (
        endpoint: string,
        seedHex: string,
        runId: string
    ) => Promise<Record<string, any>>
    adminPayerResearchRuns: (
        endpoint: string,
        seedHex: string,
        status: string,
        before: string,
        limit: number
    ) => Promise<Record<string, any>>
    adminPayerResearchRun: (
        endpoint: string,
        seedHex: string,
        runId: string
    ) => Promise<Record<string, any>>
    adminDecidePayerFinding: (
        endpoint: string,
        seedHex: string,
        runId: string,
        ordinal: number,
        decision: string
    ) => Promise<Record<string, any>>
    adminLeaveOutPayerResearch: (
        endpoint: string,
        seedHex: string,
        runId: string,
        reason: string
    ) => Promise<Record<string, any>>
    suggestAvaProviders: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        searchId: string,
        selfGrantJSON: string,
        stateSnapshotJSON: string,
        excludeProfileIdsJSON: string
    ) => Promise<{ items: Array<Record<string, any>>; stateSnapshot: Record<string, any>; keyringB64: string }>
    bindAvaSearchToAccount: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        searchId: string,
        selfGrantJSON: string,
        stateSnapshotJSON: string
    ) => Promise<{
        selfGrant: Record<string, any>
        state: Record<string, any>
        keyringB64: string
    }>
    sendAppointmentRequestInquiries: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        searchId: string,
        inquiriesJSON: string
    ) => Promise<{
        success: boolean
        error: string
        requestIds: string[]
        results: Array<{ sourceProfileId: string; sendId: string; requestId: string }>
        keyringB64: string
    }>
    listAppointmentRequests: (
        endpoint: string,
        seedHex: string,
        recipientId: string,
        state: string,
        sourceProfileId: string,
        limit: number,
        offset: number
    ) => Promise<{ items: Array<Record<string, any>> }>
    makeAppointmentOffer: (
        endpoint: string,
        seedHex: string,
        requestId: string,
        recipientIdentityId: string,
        patientIdentityId: string,
        offeredStart: string,
        durationMinutes: number,
        timezone: string,
        noteB64: string
    ) => Promise<{ offerId: string; sendId: string; requestId: string }>
    acceptAppointmentOffer: (
        endpoint: string,
        seedHex: string,
        offerId: string
    ) => Promise<{ success: boolean; requestId: string }>
    recordAppointmentOutcome: (
        endpoint: string,
        seedHex: string,
        requestId: string,
        outcome: string
    ) => Promise<{ success: boolean; requestId: string; state: string }>
    rateProvider: (
        endpoint: string,
        seedHex: string,
        requestId: string,
        dimensionsJson: string
    ) => Promise<{ success: boolean }>
    ratePatient: (
        endpoint: string,
        seedHex: string,
        requestId: string,
        score: number,
        conductTagsJson: string
    ) => Promise<{ success: boolean }>
    getMyPatientReputation: (
        endpoint: string,
        seedHex: string
    ) => Promise<{ score: number; count: number }>
    ratingsEnabled: (
        endpoint: string,
        seedHex: string
    ) => Promise<{ enabled: boolean }>
    declineAppointmentRequest: (
        endpoint: string,
        seedHex: string,
        requestId: string
    ) => Promise<{ success: boolean }>
    setAppointmentMessagesRead: (
        endpoint: string,
        seedHex: string,
        requestId: string,
        read: boolean
    ) => Promise<{ success: boolean; affected: number }>
    requestAppointmentForms: (
        endpoint: string,
        seedHex: string,
        requestId: string,
        templateIdsJSON: string
    ) => Promise<{ requestId: string; formTemplateIds: string[] }>
    recordAppointmentFormSubmission: (
        endpoint: string,
        seedHex: string,
        requestId: string,
        templateId: string,
        formSendId: string
    ) => Promise<{ submissionId: string; requestId: string }>
    declineAppointmentOffer: (
        endpoint: string,
        seedHex: string,
        offerId: string
    ) => Promise<{ success: boolean }>
    withdrawAppointmentOffer: (
        endpoint: string,
        seedHex: string,
        offerId: string
    ) => Promise<{ success: boolean }>
    sendAppointmentMessage: (
        endpoint: string,
        seedHex: string,
        requestId: string,
        recipientIdentityId: string,
        selfIdentityId: string,
        messageB64: string
    ) => Promise<{ messageId: string; sendId: string; requestId: string }>
    deleteAppointmentSearch: (
        endpoint: string,
        seedHex: string,
        searchId: string
    ) => Promise<{ success: boolean }>
    archiveAppointmentSearch: (
        endpoint: string,
        seedHex: string,
        searchId: string
    ) => Promise<{ success: boolean }>
    unarchiveAppointmentSearch: (
        endpoint: string,
        seedHex: string,
        searchId: string
    ) => Promise<{ success: boolean }>
    withdrawAppointmentSearch: (
        endpoint: string,
        seedHex: string,
        searchId: string,
        recipientId: string,
        reason?: string
    ) => Promise<{ success: boolean }>
    getAppointmentThread: (
        endpoint: string,
        seedHex: string,
        requestId: string
    ) => Promise<Record<string, any> | null>
    hasAppointmentRecipientAccess: (
        endpoint: string,
        seedHex: string
    ) => Promise<{ hasAccess: boolean }>
    hasTeamMembership: (
        endpoint: string,
        seedHex: string
    ) => Promise<{ hasTeamMembership: boolean }>
    listMyAppointmentSearches: (
        endpoint: string,
        seedHex: string
    ) => Promise<{ items: Array<Record<string, any>> }>
    listMyRequestInquiries: (
        endpoint: string,
        seedHex: string,
        searchId: string
    ) => Promise<{ items: Array<Record<string, any>> }>
    decryptSearchState: (
        seedHex: string,
        keyringB64: string,
        selfGrantJSON: string,
        stateSnapshotJSON: string
    ) => Promise<{ state: Record<string, any> }>
    getAvaTranscript: (
        endpoint: string,
        seedHex: string,
        keyringB64: string,
        searchId: string
    ) => Promise<{
        entries: Array<{
            seq: number
            role: string
            kind: string
            turn: {
                userBubble?: string
                bubbles?: string[]
                nextNodeId?: string
                input?: Record<string, any> | null
            }
        }>
        state: Record<string, any>
    }>
    adminListSubscriptions: (
        endpoint: string,
        seedHex: string,
        argsJSON: string
    ) => Promise<{ items: Array<Record<string, any>> }>
    adminProvisionSubscription: (
        endpoint: string,
        seedHex: string,
        userId: string,
        planId: string,
        quantity: number
    ) => Promise<{
        success: boolean
        error: string
        subscription?: Record<string, any>
        licenses?: Array<Record<string, any>>
    }>
    adminAssignLicense: (
        endpoint: string,
        seedHex: string,
        licenseId: string,
        userId: string
    ) => Promise<{
        success: boolean
        error: string
        license?: Record<string, any>
    }>
    adminUnassignLicense: (
        endpoint: string,
        seedHex: string,
        licenseId: string
    ) => Promise<{
        success: boolean
        error: string
        license?: Record<string, any>
    }>
    adminSearchUsers: (
        endpoint: string,
        seedHex: string,
        searchTerm: string,
        limit: number
    ) => Promise<{ items: Array<{ id: string; email: string; name: string }> }>
    adminGetIdentityDetail: (
        endpoint: string,
        seedHex: string,
        id: string
    ) => Promise<{ id: string; kind: string; name: string; email: string }>
    adminDeleteSubscription: (
        endpoint: string,
        seedHex: string,
        subscriptionId: string
    ) => Promise<{ success: boolean; error: string }>
    // Referrals (practicemd-api docs/Referrals-Architecture.md §9;
    // cmd/wasm/referral_ops.go). Filters and inputs cross as JSON strings, a
    // nullable rank as null. A refusal rejects with the server's words. The
    // referrer's public pages are not here: they read and write with a plain
    // unsigned fetch (components/referrals/publicApi.ts).
    adminListReferrers: (endpoint: string, seedHex: string, filterJSON: string) => Promise<ReferrerList>
    adminGetReferrer: (endpoint: string, seedHex: string, referrerId: string) => Promise<ReferrerDetail>
    adminCreateReferrer: (endpoint: string, seedHex: string, inputJSON: string) => Promise<CreatedReferrer>
    adminUpdateReferrer: (endpoint: string, seedHex: string, updateJSON: string) => Promise<ReferrerRecord>
    adminCreateReferralPair: (
        endpoint: string,
        seedHex: string,
        referrerId: string,
        targetKind: string,
        targetId: string,
        rank: number | null
    ) => Promise<ReferralPair>
    adminSetReferralRank: (endpoint: string, seedHex: string, linkId: string, rank: number | null) => Promise<{ success: boolean }>
    adminRevokeReferralLink: (endpoint: string, seedHex: string, linkId: string) => Promise<{ success: boolean }>
    adminDeleteReferrer: (endpoint: string, seedHex: string, referrerId: string) => Promise<{ success: boolean }>
    adminRecordReferrerTouch: (endpoint: string, seedHex: string, touchJSON: string) => Promise<ReferralPair>
    adminSetReferrerRecruiting: (
        endpoint: string,
        seedHex: string,
        referrerId: string,
        status: string,
        profileId: string
    ) => Promise<ReferrerRecord>
    adminListReferralClinicians: (endpoint: string, seedHex: string, filterJSON: string) => Promise<ReferralClinicianList>
    adminGetReferralClinician: (endpoint: string, seedHex: string, profileId: string, view: string) => Promise<ReferralClinicianDetail>
    adminListReferralTargets: (endpoint: string, seedHex: string, filterJSON: string) => Promise<ReferralTargetList>
    adminGetReferralPractice: (endpoint: string, seedHex: string, queryJSON: string) => Promise<ReferralPracticeMatrix>
    getReferralPage: (endpoint: string, seedHex: string) => Promise<ReferralPage>
    revokeReferralPair: (endpoint: string, seedHex: string, linkId: string) => Promise<{ success: boolean }>
    referralShareAs: (endpoint: string, seedHex: string, slug: string) => Promise<ReferralShareAs>
    shareReferralCode: (endpoint: string, seedHex: string, slug: string, asKind: string, asId: string) => Promise<SharedReferralCode>
    attachSharedCodes: (endpoint: string, seedHex: string, holderSecret: string) => Promise<{ success: boolean; attached: boolean }>
    adminMakeReferralCode: (endpoint: string, seedHex: string, linkId: string) => Promise<MadeReferralCode>
    adminFindAccounts: (endpoint: string, seedHex: string, query: string) => Promise<{ success: boolean; accounts: ReferrerAccountCandidate[] | null }>
    adminLinkReferrerAccount: (endpoint: string, seedHex: string, referrerId: string, accountId: string, unlink: boolean) => Promise<ReferrerAccount | null>
    // Push / WebSocket. connectPush opens the device's push socket
    // (§16.4 ticket handshake + /socket dial) and streams inbound OOBM
    // frames to onFrame as JSON strings. It returns synchronously with a
    // stop handle (the socket is long-lived, not a one-shot Promise);
    // onStatus fires once when it closes — error="" means a caller
    // stop(), non-empty means the socket dropped. onOpen, if given, fires
    // once each time the socket is (re)established, before the first frame
    // — the §16.5 reconnect catch-up seam: pull authoritative state on it,
    // since OOBMs missed while disconnected are dropped, not replayed (§8).
    // The ws endpoint is derived from the GraphQL endpoint, so pass the API
    // endpoint.
    connectPush: (
        endpoint: string,
        seedHex: string,
        onFrame: (frameJSON: string) => void,
        onStatus?: (status: { closed: boolean; error: string }) => void,
        onOpen?: () => void,
    ) => { stop: () => void; error: string }
}

declare global {
    interface Window {
        Go: any
        practicemdWASM?: PracticemdWASM
    }
}

let loadPromise: Promise<PracticemdWASM> | null = null

export function loadPracticemdWasm(): Promise<PracticemdWASM> {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('practicemd WASM cannot load on the server'))
    }
    if (window.practicemdWASM) return Promise.resolve(window.practicemdWASM)
    if (loadPromise) return loadPromise

    loadPromise = (async () => {
        await loadScript(`${wasmBase}/wasm_exec.js`)
        if (typeof window.Go !== 'function') {
            throw new Error('wasm_exec.js loaded but window.Go is missing')
        }
        const go = new window.Go()
        // Seed the Go runtime's env. PRACTICEMD_ENVIRONMENT is read by
        // device.SendEphemeralAccessEmail to decide whether to suppress
        // real SES dispatch in dev. Fall back to NODE_ENV so production
        // Gatsby builds don't accidentally suppress SES.
        //
        // Read GATSBY_*/NODE_ENV directly — Gatsby's DefinePlugin
        // inlines them at build time. The `typeof process` guard this
        // shape used to carry quietly short-circuited to 'development'
        // in production browsers (because `process` isn't defined
        // post-DefinePlugin in the browser bundle) and silently
        // suppressed SES in prod. See ENG-49 and the file-header
        // warning at top of this file.
        const practicemdEnv =
            (process.env.GATSBY_PRACTICEMD_ENVIRONMENT as string | undefined) ||
            (process.env.NODE_ENV === 'production' ? 'production' : 'development')
        go.env = { ...(go.env || {}), PRACTICEMD_ENVIRONMENT: practicemdEnv }
        // Opt-in: in development the client suppresses real SES dispatch (the
        // browser has no /tmp to write the dev artifact either). Set
        // GATSBY_PRACTICEMD_SES_ENABLED_IN_DEV=1 to actually send mail in dev —
        // e.g. to test the team-invitation email end to end. Defaults off, so
        // normal dev and e2e never send real email.
        if ((process.env.GATSBY_PRACTICEMD_SES_ENABLED_IN_DEV as string | undefined) === '1') {
            go.env = { ...go.env, PRACTICEMD_SES_ENABLED_IN_DEV: '1' }
        }
        const source = await WebAssembly.instantiateStreaming(
            // In dev the wasm lives at the STABLE /wasm/latest/device.wasm URL, so
            // the browser HTTP-caches the 20MB and a normal reload serves stale bytes
            // after `make device-wasm` overwrites them — forcing a hard refresh.
            // Revalidate (a cheap 304 when unchanged, via gatsby's Last-Modified) so a
            // fresh build loads on a normal refresh. Prod pins a real version, so the
            // path is already fingerprinted and never goes stale → leave it cached.
            fetch(`${wasmBase}/device.wasm`, deviceWasmVersion === 'latest' ? { cache: 'no-cache' } : undefined),
            go.importObject
        )
        // Main parks itself (select {}) so we intentionally don't await go.run.
        go.run(source.instance)

        const deadline = Date.now() + 3000
        while (!window.practicemdWASM && Date.now() < deadline) {
            await new Promise(r => setTimeout(r, 20))
        }
        if (!window.practicemdWASM) {
            throw new Error('WASM loaded but practicemdWASM global never appeared')
        }
        // Hand the shared library its persistence (localStore.ts). Without
        // IndexedDB the library's caches stay memory-only for the tab.
        if (hasIndexedDb() && typeof window.practicemdWASM.setLocalStore === 'function') {
            window.practicemdWASM.setLocalStore(createIndexedDbLocalStore())
        }
        return window.practicemdWASM
    })()

    loadPromise.catch(() => {
        // Don't poison the cache on failure — let callers retry.
        loadPromise = null
    })
    return loadPromise
}

function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`)
        if (existing) {
            resolve()
            return
        }
        const s = document.createElement('script')
        s.src = src
        s.onload = () => resolve()
        s.onerror = () => reject(new Error(`failed to load ${src}`))
        document.head.appendChild(s)
    })
}
